import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';

import { usePlans, type Plan } from './api';
import { describePrice, priceLabel } from './prices';

/**
 * The packages and their list prices (docs/Platform_Billing_Design.md §4.1). A list price is where a
 * company's terms start, not what any company pays: each one's agreed price is frozen onto it, so
 * changing a figure here reaches only terms set afterwards — which the subtitle says, because it is
 * the thing a person editing a price most needs to know.
 */
export function PackagesPage({
  canWrite,
  onEdit,
}: {
  canWrite: boolean;
  onEdit: (planCode: string) => void;
}) {
  const plans = usePlans();

  const columns: DataViewColumn<Plan>[] = [
    {
      title: 'Package',
      key: 'name',
      primary: true,
      render: (_, p) => (
        <>
          <span className="block font-semibold text-gray-900">{p.name}</span>
          <span className="text-default block font-mono text-[length:var(--scale-field-hint)]">
            {p.code}
          </span>
        </>
      ),
    },
    {
      title: 'List prices',
      key: 'prices',
      render: (_, p) =>
        p.prices.length === 0 ? (
          <span className="text-default">
            No list price — every company on it is priced by hand
          </span>
        ) : (
          <ul className="ps-0">
            {p.prices.map((price) => (
              <li key={`${price.chargingModel}-${price.billingPeriod}`} className="list-none">
                <span className="text-default">{priceLabel(price)}:</span> {describePrice(price)}
              </li>
            ))}
          </ul>
        ),
    },
    {
      title: '',
      key: 'edit',
      trailing: true,
      render: (_, p) =>
        canWrite && (
          <Button icon="icon icon-pencil" onClick={() => onEdit(p.code)}>
            Edit prices
          </Button>
        ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Packages"
        subtitle="List prices are where a company's terms start. A company that has agreed keeps its own price; a change here reaches only terms set afterwards."
      />
      <Card flush>
        <DataView
          columns={columns}
          dataSource={plans.data ?? []}
          loading={plans.isLoading}
          rowKey="code"
          empty={
            <EmptyState title="No packages" hint="The plan catalogue is seeded by migration." />
          }
        />
      </Card>
    </>
  );
}
